import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { X, Loader2 } from 'lucide-react';
import { api } from '../../api/client';
import DatePicker from '../DatePicker';
import { isMeterTrigger, triggerUnit, formatLastPerformed } from '../../lib/maintenanceTriggers';
import type { MaintenanceSchedule, MarkPerformedPayload } from '../../types/equipment';

const inputClass =
  'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-amber-500/50 transition-colors';
const labelClass = 'block text-xs text-fieldiq-text mb-1.5 font-medium';

interface MarkPerformedDialogProps {
  open: boolean;
  onClose: () => void;
  equipmentId: string;
  schedule: MaintenanceSchedule | null;
}

export default function MarkPerformedDialog({
  open,
  onClose,
  equipmentId,
  schedule,
}: MarkPerformedDialogProps) {
  const queryClient = useQueryClient();
  const [performedAt, setPerformedAt] = useState('');
  const [meterValue, setMeterValue] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) return;
    setPerformedAt('');
    setMeterValue('');
    setNotes('');
  }, [open, schedule?.id]);

  const meterBased = !!schedule && isMeterTrigger(schedule.triggerType);
  const unit = schedule ? triggerUnit(schedule.triggerType) : '';

  const markMutation = useMutation({
    mutationFn: () => {
      // Omit rather than null every optional field — the server defaults
      // performed_at to today and reads the meter off the unit when it can.
      const payload: MarkPerformedPayload = {};
      if (performedAt) payload.performed_at = performedAt;
      if (meterBased && meterValue) payload.meter_value = Number(meterValue);
      if (notes.trim()) payload.notes = notes.trim();
      return api.equipmentMaintenanceSchedules.markPerformed(schedule!.id, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment-maintenance-schedules', equipmentId] });
      queryClient.invalidateQueries({ queryKey: ['equipment-maintenance-due'] });
      queryClient.invalidateQueries({ queryKey: ['equipment', equipmentId] });
      toast.success(`${schedule?.name ?? 'Schedule'} marked performed`);
      onClose();
    },
    onError: (err: any) => {
      // The server rejects rewinding to an older date (stale_performed_at) and
      // meter triggers with no baseline (no_baseline_value) — both carry a
      // specific message worth showing verbatim.
      toast.error(err?.response?.data?.error || 'Failed to mark performed');
    },
  });

  if (!open || !schedule) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 overflow-y-auto py-8">
      <div className="bg-fieldiq-card border border-fieldiq-border rounded-2xl w-full max-w-md mx-4 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-fieldiq-border">
          <div>
            <h3 className="text-lg font-bold text-white">Mark Performed</h3>
            <p className="text-xs text-fieldiq-text mt-0.5">{schedule.name}</p>
          </div>
          <button onClick={onClose} className="text-fieldiq-muted hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div className="bg-fieldiq-dark rounded-lg px-3 py-2.5 text-xs">
            <span className="text-fieldiq-muted">Last performed: </span>
            <span className="text-white font-medium">{formatLastPerformed(schedule)}</span>
          </div>

          <div>
            <label className={labelClass}>Date performed</label>
            <DatePicker value={performedAt} onChange={setPerformedAt} placeholder="Today" />
            <p className="text-[11px] text-fieldiq-muted mt-1">
              Leave empty to use today. The server refuses dates earlier than the last recorded
              service.
            </p>
          </div>

          {meterBased && (
            <div>
              <label className={labelClass}>Meter reading at service ({unit})</label>
              <input
                className={inputClass}
                type="number"
                min={0}
                placeholder="Optional"
                value={meterValue}
                onChange={(e) => setMeterValue(e.target.value)}
              />
              <p className="text-[11px] text-fieldiq-muted mt-1">
                Optional when the unit already has a {unit} reading on or before this date —
                required otherwise.
              </p>
            </div>
          )}

          <div>
            <label className={labelClass}>Notes (optional)</label>
            <input
              className={inputClass}
              placeholder="e.g. Filter replaced, no leaks"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-fieldiq-border">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-fieldiq-border text-fieldiq-muted rounded-lg text-sm font-medium hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => markMutation.mutate()}
            disabled={markMutation.isPending}
            className="flex items-center gap-2 bg-fieldiq-accent text-fieldiq-dark font-semibold px-5 py-2 rounded-lg text-sm hover:bg-amber-400 transition-colors disabled:opacity-50"
          >
            {markMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Mark Performed
          </button>
        </div>
      </div>
    </div>
  );
}
